import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { getRegistrationLink, sendPatientInvites, getPatientInvites, createPatient } from '../utils/api';
import { useWindowSize } from '../utils/useWindowSize';
import colors from '../styles/colors';

const PatientOnboarding = () => {
  const { isMobile } = useWindowSize();

  // Section 1 — Registration Link
  const [linkData, setLinkData]       = useState(null);
  const [linkLoading, setLinkLoading] = useState(true);
  const [linkError, setLinkError]     = useState('');
  const [copied, setCopied]           = useState(false);

  // Section 2 — Invite Patients
  const [inviteRows, setInviteRows]     = useState([{ name: '', phone: '' }]);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  // Section 3 — Invite Tracking
  const [invites, setInvites]         = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(true);

  // Section 4 — Create Patient
  const [createForm, setCreateForm]   = useState({ firstName: '', lastName: '', mobile: '', email: '', condition: '' });
  const [creating, setCreating]       = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [createError, setCreateError] = useState('');

  const qrAnchorRef = useRef(null);

  // -------------------------------------------------------------------------
  // Load registration link and invite list on mount
  // -------------------------------------------------------------------------
  useEffect(function() {
    (async function() {
      try {
        const res = await getRegistrationLink();
        setLinkData(res.data.data);
      } catch {
        setLinkError('Could not load registration link. Please refresh.');
      } finally {
        setLinkLoading(false);
      }
    })();
    loadInvites();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadInvites() {
    setInvitesLoading(true);
    try {
      const res = await getPatientInvites();
      setInvites(res.data.data || []);
    } catch {
      setInvites([]);
    } finally {
      setInvitesLoading(false);
    }
  }

  // -------------------------------------------------------------------------
  // Section 1 handlers
  // -------------------------------------------------------------------------
  function handleCopyLink() {
    if (!linkData) return;
    navigator.clipboard.writeText(linkData.url);
    setCopied(true);
    setTimeout(function() { setCopied(false); }, 2000);
  }

  function handleDownloadQR() {
    if (!linkData) return;
    const anchor = qrAnchorRef.current;
    anchor.href     = linkData.qrCodeBase64;
    anchor.download = `lifechiai-qr-${linkData.centreCode}.png`;
    anchor.click();
  }

  // -------------------------------------------------------------------------
  // Section 2 handlers
  // -------------------------------------------------------------------------
  function handleInviteRowChange(index, field, value) {
    setInviteRows(function(prev) {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setInviteResult(null);
  }

  function handleAddRow() {
    setInviteRows(function(prev) { return [...prev, { name: '', phone: '' }]; });
  }

  async function handleSendInvites() {
    const valid = inviteRows.filter(function(r) { return r.name.trim() && r.phone.trim(); });
    if (valid.length === 0) return;

    setInviteSending(true);
    setInviteResult(null);

    try {
      const res = await sendPatientInvites(valid);
      setInviteResult(res.data.data);
      setInviteRows([{ name: '', phone: '' }]);
      loadInvites();
    } catch (err) {
      setInviteResult({ sent: 0, failed: [], error: err.response?.data?.message || 'Send failed' });
    } finally {
      setInviteSending(false);
    }
  }

  // -------------------------------------------------------------------------
  // Section 4 handlers
  // -------------------------------------------------------------------------
  function handleCreateChange(field, value) {
    setCreateForm(function(prev) { return { ...prev, [field]: value }; });
    setCreateResult(null);
    setCreateError('');
  }

  async function handleCreatePatient() {
    if (!createForm.firstName || !createForm.lastName || !createForm.mobile) {
      setCreateError('First name, last name, and mobile are required.');
      return;
    }

    setCreating(true);
    setCreateResult(null);
    setCreateError('');

    try {
      const res = await createPatient(createForm);
      setCreateResult(res.data.data);
      setCreateForm({ firstName: '', lastName: '', mobile: '', email: '', condition: '' });
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Could not create patient.');
    } finally {
      setCreating(false);
    }
  }

  // -------------------------------------------------------------------------
  // Status badge
  // -------------------------------------------------------------------------
  function StatusBadge({ status }) {
    const badgeColors = {
      sent:       { background: '#fef3c7', color: '#92400e' },
      registered: { background: '#d1fae5', color: '#065f46' },
      expired:    { background: '#f3f4f6', color: '#6b7280' }
    };
    const style = badgeColors[status] || badgeColors.expired;
    return (
      <span style={{
        ...styles.badge,
        background: style.background,
        color:      style.color
      }}>
        {status}
      </span>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={{ ...styles.main, padding: isMobile ? '16px' : '32px' }}>
        <h1 style={styles.pageTitle}>Patient Onboarding</h1>

        {/* ----------------------------------------------------------------- */}
        {/* Section 1 — Registration Link                                      */}
        {/* ----------------------------------------------------------------- */}
        <section style={styles.card}>
          <h2 style={styles.sectionTitle}>Centre Registration Link</h2>

          {linkLoading && <p style={styles.muted}>Loading...</p>}
          {linkError   && <p style={styles.error}>{linkError}</p>}

          {linkData && (
            <>
              <div style={styles.row}>
                <input
                  readOnly
                  value={linkData.url}
                  style={{ ...styles.input, flex: 1, background: '#f9fafb' }}
                />
                <button onClick={handleCopyLink} style={styles.btnSecondary}>
                  {copied ? 'Copied' : 'Copy Link'}
                </button>
                <button onClick={handleDownloadQR} style={styles.btnPrimary}>
                  Download QR
                </button>
              </div>

              <div style={styles.qrWrap}>
                <img
                  src={linkData.qrCodeBase64}
                  alt="Centre QR code"
                  style={styles.qrImage}
                />
              </div>

              {/* Hidden anchor for QR download */}
              <a ref={qrAnchorRef} style={{ display: 'none' }} href="/#">download</a>
            </>
          )}
        </section>

        {/* ----------------------------------------------------------------- */}
        {/* Section 2 — Invite Patients                                        */}
        {/* ----------------------------------------------------------------- */}
        <section style={styles.card}>
          <h2 style={styles.sectionTitle}>Invite Patients</h2>

          {inviteRows.map(function(row, idx) {
            return (
              <div key={idx} style={{ ...styles.row, marginBottom: 8 }}>
                <input
                  placeholder="Name"
                  value={row.name}
                  onChange={function(e) { handleInviteRowChange(idx, 'name', e.target.value); }}
                  style={{ ...styles.input, flex: 1 }}
                />
                <input
                  placeholder="Phone (E.164)"
                  value={row.phone}
                  onChange={function(e) { handleInviteRowChange(idx, 'phone', e.target.value); }}
                  style={{ ...styles.input, flex: 1 }}
                />
              </div>
            );
          })}

          <div style={styles.row}>
            <button onClick={handleAddRow} style={styles.btnSecondary}>+ Add another</button>
            <button
              onClick={handleSendInvites}
              disabled={inviteSending}
              style={styles.btnPrimary}
            >
              {inviteSending ? 'Sending...' : 'Send Invites'}
            </button>
          </div>

          {inviteResult && (
            <div style={inviteResult.error ? styles.bannerError : styles.bannerSuccess}>
              {inviteResult.error
                ? inviteResult.error
                : `${inviteResult.sent} invite${inviteResult.sent !== 1 ? 's' : ''} sent`
              }
              {inviteResult.failed && inviteResult.failed.length > 0 && (
                <ul style={styles.failList}>
                  {inviteResult.failed.map(function(f, i) {
                    return <li key={i}>{f.name} ({f.phone}) — {f.error}</li>;
                  })}
                </ul>
              )}
            </div>
          )}
        </section>

        {/* ----------------------------------------------------------------- */}
        {/* Section 3 — Invite Tracking                                        */}
        {/* ----------------------------------------------------------------- */}
        <section style={styles.card}>
          <h2 style={styles.sectionTitle}>Invite Tracking</h2>

          {invitesLoading
            ? <p style={styles.muted}>Loading...</p>
            : invites.length === 0
              ? <p style={styles.muted}>No invites sent yet.</p>
              : (
                <div style={styles.tableWrap}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        {['Name', 'Phone', 'Sent', 'Status', 'Registered'].map(function(h) {
                          return <th key={h} style={styles.th}>{h}</th>;
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {invites.map(function(inv, i) {
                        return (
                          <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                            <td style={styles.td}>{inv.name}</td>
                            <td style={styles.td}>{inv.phone}</td>
                            <td style={styles.td}>{inv.sentAt ? new Date(inv.sentAt).toLocaleDateString() : '—'}</td>
                            <td style={styles.td}><StatusBadge status={inv.status} /></td>
                            <td style={styles.td}>{inv.registeredAt ? new Date(inv.registeredAt).toLocaleDateString() : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
          }
        </section>

        {/* ----------------------------------------------------------------- */}
        {/* Section 4 — Create Patient                                         */}
        {/* ----------------------------------------------------------------- */}
        <section style={styles.card}>
          <h2 style={styles.sectionTitle}>Create Patient</h2>

          <div style={isMobile ? styles.formColMobile : styles.formRow}>
            <div style={styles.formField}>
              <label style={styles.label}>First Name *</label>
              <input
                value={createForm.firstName}
                onChange={function(e) { handleCreateChange('firstName', e.target.value); }}
                style={styles.input}
                placeholder="Priya"
              />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>Last Name *</label>
              <input
                value={createForm.lastName}
                onChange={function(e) { handleCreateChange('lastName', e.target.value); }}
                style={styles.input}
                placeholder="Sharma"
              />
            </div>
          </div>

          <div style={isMobile ? styles.formColMobile : styles.formRow}>
            <div style={styles.formField}>
              <label style={styles.label}>Mobile * (E.164)</label>
              <input
                value={createForm.mobile}
                onChange={function(e) { handleCreateChange('mobile', e.target.value); }}
                style={styles.input}
                placeholder="+919876543210"
              />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>Email (optional)</label>
              <input
                value={createForm.email}
                onChange={function(e) { handleCreateChange('email', e.target.value); }}
                style={styles.input}
                placeholder="priya@example.com"
              />
            </div>
          </div>

          <div style={styles.formField}>
            <label style={styles.label}>Condition (optional)</label>
            <input
              value={createForm.condition}
              onChange={function(e) { handleCreateChange('condition', e.target.value); }}
              style={styles.input}
              placeholder="e.g. Chronic fatigue"
            />
          </div>

          {createError  && <p style={styles.error}>{createError}</p>}

          {createResult && (
            <div style={styles.bannerSuccess}>
              Patient created: {createResult.firstName} {createResult.lastName}
            </div>
          )}

          <button
            onClick={handleCreatePatient}
            disabled={creating}
            style={{ ...styles.btnPrimary, marginTop: 12 }}
          >
            {creating ? 'Creating...' : 'Create & Notify'}
          </button>
        </section>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = {
  layout:       { display: 'flex', minHeight: '100vh', background: '#f9fafb' },
  main:         { flex: 1, overflowX: 'hidden' },
  pageTitle:    { fontSize: 24, fontWeight: 700, marginBottom: 24, color: colors?.text || '#111827' },
  card:         { background: '#fff', borderRadius: 8, padding: 24, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  sectionTitle: { fontSize: 18, fontWeight: 600, marginBottom: 16, color: colors?.text || '#111827' },
  row:          { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  formRow:      { display: 'flex', gap: 16, marginBottom: 12 },
  formColMobile:{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 },
  formField:    { flex: 1, display: 'flex', flexDirection: 'column' },
  label:        { fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#374151' },
  input:        { padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 15, minHeight: 44, boxSizing: 'border-box', width: '100%' },
  btnPrimary:   { padding: '10px 18px', background: colors?.primary || '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 44 },
  btnSecondary: { padding: '10px 18px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 44 },
  qrWrap:       { marginTop: 16, display: 'flex', justifyContent: 'flex-start' },
  qrImage:      { width: 180, height: 180, borderRadius: 4, border: '1px solid #e5e7eb' },
  tableWrap:    { overflowX: 'auto' },
  table:        { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th:           { textAlign: 'left', padding: '10px 12px', background: '#f3f4f6', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' },
  td:           { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151' },
  trEven:       { background: '#fff' },
  trOdd:        { background: '#fafafa' },
  badge:        { display: 'inline-block', padding: '2px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600 },
  bannerSuccess:{ marginTop: 12, padding: '10px 14px', background: '#d1fae5', color: '#065f46', borderRadius: 6, fontSize: 14 },
  bannerError:  { marginTop: 12, padding: '10px 14px', background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 14 },
  failList:     { margin: '6px 0 0 0', paddingLeft: 16, fontSize: 13 },
  muted:        { color: '#9ca3af', fontSize: 14 },
  error:        { color: '#dc2626', fontSize: 14, marginBottom: 8 }
};

export default PatientOnboarding;
